import { DocumentNode, gql } from '@apollo/client';
import { Box, Grid, GridItem, Heading, Stack } from '@chakra-ui/react';
import { ContentState, convertToRaw, RawDraftContentState } from 'draft-js';
import NextLink from 'next/link';
import * as React from 'react';

import { DeprecatedDialInInfo } from '@/components/DialInInfo';
import { Redraft } from '@/components/Redraft';
import { usePermissions } from '@/hooks/usePermissions';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { SpeakerCard } from '@/imports/ui/components/SpeakerCard';

import { VideoDetailCardPresentational_UserFragment } from './graphql-types/VideoDetailCardPresentational_UserFragment';

import { VideoDetailCardSponsorSection } from './VideoDetailCardSponsorSection';

export type ISponsor = {
  _id: string;
  name: string;
  profilePicture: { imageProxy: string | null } | null;
};

type IVideoDetailCardPresentationalProps = {
  description?: string | null;
  descriptionRichText?: RawDraftContentState | null;
  sponsors?: ISponsor[] | null;
  speakerEntityName?: string;
  dialInEnabled?: boolean | null;
  eventName?: string | null;
  dialInCode?: string | null;
  listenerDialInCode?: string | null;
  dialInPhoneNumber?: string | null;
  isCurrentUserSpeaker?: boolean | null;
  isParticipationAllowed?: boolean | null;
  showDialInInfo?: boolean;
};

export const VideoDetailCardPresentational: React.FC<
  IVideoDetailCardPresentationalProps & {
    speakers: VideoDetailCardPresentational_UserFragment[];
  }
> & { userFragment: DocumentNode } = (props) => {
  const {
    dialInEnabled,
    dialInCode,
    listenerDialInCode,
    eventName,
    dialInPhoneNumber,
    description,
    speakerEntityName,
    speakers,
    sponsors,
    descriptionRichText,
    isCurrentUserSpeaker,
    isParticipationAllowed,
  } = props;

  const descriptionContent = React.useMemo(() => {
    if (descriptionRichText) return descriptionRichText;
    if (!description) return null;
    if (typeof description === 'string') {
      try {
        return JSON.parse(description);
      } catch {
        return convertToRaw(ContentState.createFromText(description));
      }
    }
    return description;
  }, [description, descriptionRichText]);

  const hasDescriptionRichText = !!descriptionContent?.blocks?.find(
    (block : any) => !!block.text.length
  );

  const hasSpeakers = !!speakers && speakers.length > 0;
  const hasSponsors = !!sponsors && sponsors.length > 0;

  if (
    !hasDescriptionRichText &&
    !hasSpeakers &&
    !hasSponsors &&
    (!dialInEnabled || !props.showDialInInfo)
  ) {
    return null;
  }

  const dialInSection = props.showDialInInfo && (
    <DeprecatedDialInInfo
      eventName={eventName}
      dialInCode={dialInCode}
      listenerDialInCode={listenerDialInCode}
      isCurrentUserSpeaker={isCurrentUserSpeaker}
      isParticipationAllowed={isParticipationAllowed}
      dialInEnabled={dialInEnabled}
      dialInPhoneNumber={dialInPhoneNumber}
    />
  );

  const descriptionSection = hasDescriptionRichText ? (
    <Stack spacing="4">
      <Heading fontSize="md">Description</Heading>
      <Prose wordBreak="break-word" color="gray.600" mt="4">
        <Redraft rawDraftContentState={descriptionContent!} />
      </Prose>
      {dialInSection}
    </Stack>
  ) : (
    <Stack spacing="4">{dialInSection}</Stack>
  );

  const speakerSection = hasSpeakers && (
    <Stack spacing="4">
      <Heading fontSize="md">
        {!!speakerEntityName ? speakerEntityName : 'Speakers'}
      </Heading>
      <Stack>
        <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap="4">
          {speakers.map((speaker) => {
            return (
              <NextLink href={`/members/${speaker._id}`} key={speaker._id}>
                <GridItem>
                  <SpeakerCard user={speaker} />
                </GridItem>
              </NextLink>
            );
          })}
        </Grid>
      </Stack>
    </Stack>
  );

  const sponsorSection = hasSponsors && (
    <VideoDetailCardSponsorSection
      hasSpeakers={hasSpeakers}
      sponsors={sponsors}
    />
  );

  return (
    <Card p="6">
      <Stack spacing="8">
        {descriptionSection}
        {speakerSection}
        {sponsorSection}
      </Stack>
    </Card>
  );
};

VideoDetailCardPresentational.userFragment = gql`
  fragment VideoDetailCardPresentational_UserFragment on User {
    _id
    ...SpeakerCard_UserFragment
  }
  ${SpeakerCard.userFragment}
`;
